import React, { useState, useEffect } from 'react';
import {
  Card,
  Table,
  Button,
  Modal,
  Form,
  Input,
  Switch,
  Select,
  message,
  Popconfirm,
  Space,
  Tag,
  Tabs
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined
} from '@ant-design/icons';
import api from '../utils/api';
import API_ENDPOINTS from '../config/api';

const { TabPane } = Tabs;
const { Option } = Select;

const CategoryManagement = () => {
  const [categories, setCategories] = useState([]);
  const [subcategories, setSubcategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [categoryModalVisible, setCategoryModalVisible] = useState(false);
  const [subcategoryModalVisible, setSubcategoryModalVisible] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [editingSubcategory, setEditingSubcategory] = useState(null);
  const [categoryForm] = Form.useForm();
  const [subcategoryForm] = Form.useForm();
  const [activeTab, setActiveTab] = useState('categories');

  useEffect(() => {
    loadData();
  }, [activeTab]);

  const loadData = async () => {
    setLoading(true);
    try {
      if (activeTab === 'categories') {
        await loadCategories();
      } else {
        await loadSubcategories();
      }
    } catch (error) {
      console.error('Failed to load data:', error);
      message.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const loadCategories = async () => {
    try {
      const response = await api.get(API_ENDPOINTS.CATEGORIES());
      setCategories(response.data?.data || []);
    } catch (error) {
      console.error('Failed to load categories:', error);
      message.error('Failed to load categories');
    }
  };

  const loadSubcategories = async () => {
    try {
      const response = await api.get(API_ENDPOINTS.SUBCATEGORIES());
      setSubcategories(response.data?.data || []);
    } catch (error) {
      console.error('Failed to load subcategories:', error);
      message.error('Failed to load subcategories');
    }
  };

  // Category CRUD functions
  const handleAddCategory = () => {
    setEditingCategory(null);
    categoryForm.resetFields();
    setCategoryModalVisible(true);
  };

  const handleEditCategory = (record) => {
    setEditingCategory(record);
    categoryForm.setFieldsValue({
      category_name: record.category_name,
      category_code: record.category_code,
      description: record.description,
      is_active: record.is_active,
      flow_type: record.flow_type || 'cash_in'
    });
    setCategoryModalVisible(true);
  };

  const handleDeleteCategory = async (record) => {
    setLoading(true);
    try {
      await api.delete(`${API_ENDPOINTS.CATEGORIES()}${record.id}`);
      message.success('Category deleted successfully');
      loadCategories();
    } catch (error) {
      console.error('Delete category error:', error);
      message.error(error.response?.data?.message || 'Failed to delete category');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitCategory = async (values) => {
    setLoading(true);
    try {
      if (editingCategory) {
        await api.put(`${API_ENDPOINTS.CATEGORIES()}${editingCategory.id}`, values);
        message.success('Category updated successfully');
      } else {
        await api.post(API_ENDPOINTS.CATEGORIES(), values);
        message.success('Category created successfully');
      }

      setCategoryModalVisible(false);
      loadCategories();
    } catch (error) {
      console.error('Category form submission error:', error);
      message.error(error.response?.data?.message || 'Failed to save category');
    } finally {
      setLoading(false);
    }
  };

  // Subcategory CRUD functions
  const handleAddSubcategory = () => {
    setEditingSubcategory(null);
    subcategoryForm.resetFields();
    setSubcategoryModalVisible(true);
  };

  const handleEditSubcategory = (record) => {
    setEditingSubcategory(record);
    subcategoryForm.setFieldsValue({
      subcategory_name: record.subcategory_name,
      subcategory_code: record.subcategory_code,
      category_id: record.category_id,
      description: record.description,
      is_active: record.is_active
    });
    setSubcategoryModalVisible(true);
  };

  const handleDeleteSubcategory = async (record) => {
    setLoading(true);
    try {
      await api.delete(`${API_ENDPOINTS.SUBCATEGORIES()}${record.id}`);
      message.success('Subcategory deleted successfully');
      loadSubcategories();
    } catch (error) {
      console.error('Delete subcategory error:', error);
      message.error(error.response?.data?.message || 'Failed to delete subcategory');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitSubcategory = async (values) => {
    setLoading(true);
    try {
      if (editingSubcategory) {
        await api.put(`${API_ENDPOINTS.SUBCATEGORIES()}${editingSubcategory.id}`, values);
        message.success('Subcategory updated successfully');
      } else {
        await api.post(API_ENDPOINTS.SUBCATEGORIES(), values);
        message.success('Subcategory created successfully');
      }

      setSubcategoryModalVisible(false);
      loadSubcategories();
    } catch (error) {
      console.error('Subcategory form submission error:', error);
      message.error(error.response?.data?.message || 'Failed to save subcategory');
    } finally {
      setLoading(false);
    }
  };

  const categoryColumns = [
    {
      title: 'Category Name',
      dataIndex: 'category_name',
      key: 'category_name'
    },
    {
      title: 'Category Code',
      dataIndex: 'category_code',
      key: 'category_code',
      render: (code) => <Tag color="blue">{code}</Tag>
    },
    {
      title: 'Tipe Transaksi',
      dataIndex: 'flow_type',
      key: 'flow_type',
      render: (flow) => (
        <Tag color={flow === 'cash_in' ? 'green' : 'orange'}>
          {flow === 'cash_in' ? 'Cash In' : 'Cash Out'}
        </Tag>
      )
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description'
    },
    {
      title: 'Products Count',
      key: 'products_count',
      render: (_, record) => (
        <Tag color="green">{record.products_count || 0}</Tag>
      )
    },
    {
      title: 'Status',
      dataIndex: 'is_active',
      key: 'is_active',
      render: (active) => (
        <Tag color={active ? 'green' : 'red'}>
          {active ? 'Active' : 'Inactive'}
        </Tag>
      )
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Button
            type="primary"
            size="small"
            icon={<EditOutlined />}
            onClick={() => handleEditCategory(record)}
          >
            Edit
          </Button>
          <Popconfirm
            title="Are you sure you want to delete this category?"
            description="This action cannot be undone."
            onConfirm={() => handleDeleteCategory(record)}
            okText="Yes"
            cancelText="No"
          >
            <Button
              type="danger"
              size="small"
              icon={<DeleteOutlined />}
            >
              Delete
            </Button>
          </Popconfirm>
        </Space>
      )
    }
  ];

  const subcategoryColumns = [
    {
      title: 'Category',
      dataIndex: 'category_name',
      key: 'category_name',
      render: (text, record) => (
        <div>
          <div style={{ fontWeight: 'bold' }}>{text}</div>
          <div style={{ fontSize: '12px', color: '#666' }}>{record.category_code}</div>
        </div>
      )
    },
    {
      title: 'Subcategory Name',
      dataIndex: 'subcategory_name',
      key: 'subcategory_name'
    },
    {
      title: 'Subcategory Code',
      dataIndex: 'subcategory_code',
      key: 'subcategory_code',
      render: (code) => <Tag color="blue">{code}</Tag>
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description'
    },
    {
      title: 'Products Count',
      key: 'products_count',
      render: (_, record) => (
        <Tag color="green">{record.products_count || 0}</Tag>
      )
    },
    {
      title: 'Status',
      dataIndex: 'is_active',
      key: 'is_active',
      render: (active) => (
        <Tag color={active ? 'green' : 'red'}>
          {active ? 'Active' : 'Inactive'}
        </Tag>
      )
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Button
            type="primary"
            size="small"
            icon={<EditOutlined />}
            onClick={() => handleEditSubcategory(record)}
          >
            Edit
          </Button>
          <Popconfirm
            title="Are you sure you want to delete this subcategory?"
            description="This action cannot be undone."
            onConfirm={() => handleDeleteSubcategory(record)}
            okText="Yes"
            cancelText="No"
          >
            <Button
              type="danger"
              size="small"
              icon={<DeleteOutlined />}
            >
              Delete
            </Button>
          </Popconfirm>
        </Space>
      )
    }
  ];

  return (
    <div>
      <Card title="Category Management" style={{ marginBottom: 16 }}>
        <Tabs activeKey={activeTab} onChange={setActiveTab}>
          <TabPane tab="Categories" key="categories">
            <div style={{ marginBottom: 16 }}>
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={handleAddCategory}
              >
                Add Category
              </Button>
            </div>
            <Table
              columns={categoryColumns}
              dataSource={categories}
              rowKey="id"
              loading={loading}
              pagination={false}
            />
          </TabPane>

          <TabPane tab="Subcategories" key="subcategories">
            <div style={{ marginBottom: 16 }}>
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={handleAddSubcategory}
              >
                Add Subcategory
              </Button>
            </div>
            <Table
              columns={subcategoryColumns}
              dataSource={subcategories}
              rowKey="id"
              loading={loading}
              pagination={false}
            />
          </TabPane>
        </Tabs>
      </Card>

      {/* Category Modal */}
      <Modal
        title={editingCategory ? 'Edit Category' : 'Add Category'}
        visible={categoryModalVisible}
        onCancel={() => setCategoryModalVisible(false)}
        footer={null}
        width={500}
      >
        <Form
          form={categoryForm}
          layout="vertical"
          onFinish={handleSubmitCategory}
        >
          <Form.Item
            name="category_name"
            label="Category Name"
            rules={[{ required: true, message: 'Please enter category name' }]}
          >
            <Input placeholder="Enter category name" />
          </Form.Item>

          <Form.Item
            name="category_code"
            label="Category Code"
            rules={[{ required: true, message: 'Please enter category code' }]}
          >
            <Input
              placeholder="Enter category code"
              style={{ textTransform: 'uppercase' }}
              disabled={!!editingCategory}
            />
          </Form.Item>

          <Form.Item
            name="flow_type"
            label="Tipe Transaksi"
            initialValue="cash_in"
            rules={[{ required: true }]}
          >
            <Select>
              <Option value="cash_in">Cash In (uang masuk)</Option>
              <Option value="cash_out">Cash Out (kurangi saldo deposit)</Option>
            </Select>
          </Form.Item>

          <Form.Item
            name="description"
            label="Description"
          >
            <Input.TextArea placeholder="Enter category description" rows={3} />
          </Form.Item>

          <Form.Item
            name="is_active"
            label="Status"
            valuePropName="checked"
          >
            <Switch
              checkedChildren="Active"
              unCheckedChildren="Inactive"
            />
          </Form.Item>

          <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
            <Space>
              <Button onClick={() => setCategoryModalVisible(false)}>
                Cancel
              </Button>
              <Button type="primary" htmlType="submit" loading={loading}>
                {editingCategory ? 'Update' : 'Create'}
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* Subcategory Modal */}
      <Modal
        title={editingSubcategory ? 'Edit Subcategory' : 'Add Subcategory'}
        visible={subcategoryModalVisible}
        onCancel={() => setSubcategoryModalVisible(false)}
        footer={null}
        width={500}
      >
        <Form
          form={subcategoryForm}
          layout="vertical"
          onFinish={handleSubmitSubcategory}
        >
          <Form.Item
            name="category_id"
            label="Category"
            rules={[{ required: true, message: 'Please select a category' }]}
          >
            <Select placeholder="Select category">
              {categories.map(category => (
                <Option key={category.id} value={category.id}>
                  {category.category_name} ({category.category_code})
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="subcategory_name"
            label="Subcategory Name"
            rules={[{ required: true, message: 'Please enter subcategory name' }]}
          >
            <Input placeholder="Enter subcategory name" />
          </Form.Item>

          <Form.Item
            name="subcategory_code"
            label="Subcategory Code"
            rules={[{ required: true, message: 'Please enter subcategory code' }]}
          >
            <Input
              placeholder="Enter subcategory code"
              style={{ textTransform: 'uppercase' }}
              disabled={!!editingSubcategory}
            />
          </Form.Item>

          <Form.Item
            name="description"
            label="Description"
          >
            <Input.TextArea placeholder="Enter subcategory description" rows={3} />
          </Form.Item>

          <Form.Item
            name="is_active"
            label="Status"
            valuePropName="checked"
          >
            <Switch
              checkedChildren="Active"
              unCheckedChildren="Inactive"
            />
          </Form.Item>

          <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
            <Space>
              <Button onClick={() => setSubcategoryModalVisible(false)}>
                Cancel
              </Button>
              <Button type="primary" htmlType="submit" loading={loading}>
                {editingSubcategory ? 'Update' : 'Create'}
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default CategoryManagement;